import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface AdminRouteProps {
  children: ReactNode
}

/**
 * AdminRoute - Protects admin-only routes
 *
 * Checks if the authenticated user has admin privileges by querying
 * the profiles.is_admin field in the database.
 *
 * To make a user an admin, run in Supabase SQL Editor:
 * UPDATE profiles SET is_admin = TRUE WHERE email = 'user@example.com';
 */

async function checkIsAdmin(userId: string): Promise<boolean> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error checking admin status:', error)
      return false
    }

    return profile?.is_admin || false
  } catch (error) {
    console.error('Exception checking admin status:', error)
    return false
  }
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading } = useAuth()

  const { data: isAdmin, isLoading: checkingAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: () => checkIsAdmin(user!.id),
    enabled: !!user,
  })

  if (loading || checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the admin dashboard.
            Please contact an AionTrack administrator if you believe this is an error.
          </p>
          <a
            href="/projects"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Return to Projects
          </a>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
